// The clan identity pack on the screen (monetization.md §3.3, R3290).
//
// Two looks, one key (`clan_identity`):
//
//   - The **chat ink** colours the name of a member in the clan chat: the
//     hub CLAN chat and the CLAN tab of the game chat. The name text does
//     not change, and an ink is never a faction colour, so the colour
//     tells no side and no signal (themes.md §10.1). A world channel keeps
//     the faction colour of the sender.
//   - The **banner style** is a frame or a pattern of the clan banner. It
//     draws in the faction ink through the `.bnst.bn-<code>` rule of
//     hub.css and changes no colour.
//
// Every reader sees both. Only a pick needs the key, and the server holds
// that rule. A code this build does not know reads as no pick.

import { BANNER_STYLES, CHAT_INKS, type BannerStyleCode, type ChatInkCode } from '../gen/presentation';

const INK_OF: ReadonlyMap<string, string> = new Map(CHAT_INKS.map((r) => [r.code, r.ink]));

/** A stored chat ink as a pick: a code of the list, or '' for none. */
export function asChatInk(code: string | undefined): ChatInkCode | '' {
  return code !== undefined && INK_OF.has(code) ? code as ChatInkCode : '';
}

/** A stored banner style as a pick: a code of the list, or '' for PLAIN. */
export function asBannerStyle(code: string | undefined): BannerStyleCode | '' {
  return code !== undefined && (BANNER_STYLES as readonly string[]).includes(code) ? code as BannerStyleCode : '';
}

/** The colour of one chat ink, or '' for none. */
export function chatInkHex(code: string | undefined): string {
  return INK_OF.get(asChatInk(code)) ?? '';
}

/** The style attribute that colours a name in the chat ink, with its
 *  leading space, or '' for a line with no ink. */
export function inkStyle(code: string | undefined): string {
  const hex = chatInkHex(code);
  return hex === '' ? '' : ` style="color:${hex}"`;
}

/** The classes of a banner in its style, with a leading space, or '' for
 *  PLAIN. */
export function bannerClass(style: string | undefined): string {
  const s = asBannerStyle(style);
  return s === '' ? '' : ` bnst bn-${s}`;
}

/** The clanLook string key of the name of a chat ink. */
export function chatInkNameKey(code: ChatInkCode): `chat_ink_name_${ChatInkCode}` {
  return `chat_ink_name_${code}`;
}

/** The clanLook string key of the name of a banner style. */
export function bannerStyleNameKey(code: BannerStyleCode): `banner_style_name_${BannerStyleCode}` {
  return `banner_style_name_${code}`;
}
